// WhatsApp Business (ideas track, idea 36) — the CRM side, pure.
//
// The advisor connects a WhatsApp Business Platform (Cloud API) number on the platform (token and phone-number id are
// stored sealed there, never in the CRM). In the CRM he names the APPROVED Meta template for each use — appointment
// reminder, birthday wishes, renewal notice — and their language. Messages go:
//   - only to clients with a WhatsApp consent recorded (consents: purpose `whatsapp`, granted, not withdrawn — a later
//     withdrawal always wins) and a mobile number;
//   - only when the advisor presses «Send on WhatsApp», or when he turns on the automation for that one use (each OFF
//     by default); automations prepare a queue the platform sends — once per client and occasion (stable key);
//   - with only the template's parameters (first name, date, time / product name): never amounts, policy numbers or
//     health details.
// Each message is recorded on the client's timeline as a WhatsApp communication with its delivery status (sent →
// delivered → read, or failed) that the platform receives from Meta's webhook. Settings `whatsapp` (no schema change:
// communications.data carries the WhatsApp message id and status).
import { stableId } from './ids.ts';
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, clean, created, put, requirePerson, timeline, touched, type Ctx } from './cmd.ts';
import { getSetting } from './services.ts';
import { diffDays, type Day } from './dates.ts';
import { renewalQueue } from './business.ts';
import type { Person } from './schema.ts';

export const WHATSAPP_USES = ['appointment_reminder', 'birthday', 'renewal_notice'] as const;
export type WhatsAppUse = typeof WHATSAPP_USES[number];
export const WHATSAPP_KEY = 'whatsapp';
export const WHATSAPP_CONSENT = 'whatsapp';
export const WA_STATUSES = ['queued', 'sent', 'delivered', 'read', 'failed'] as const;
export type WaStatus = typeof WA_STATUSES[number];

export interface WhatsAppTemplateSetting { name: string; lang: string; auto: boolean }
export interface WhatsAppSettings {
  /** the CRM may use WhatsApp at all (the number itself is connected on the platform) */
  enabled: boolean;
  templates: Record<WhatsAppUse, WhatsAppTemplateSetting>;
  /** reminder: hours before the appointment (automation) */
  reminderHours: number;
  /** renewal notice: days before the renewal date (automation) */
  renewalDays: number;
}
export const DEFAULT_WHATSAPP: WhatsAppSettings = {
  enabled: false, reminderHours: 24, renewalDays: 21,
  templates: { appointment_reminder: { name: '', lang: 'el', auto: false }, birthday: { name: '', lang: 'el', auto: false }, renewal_notice: { name: '', lang: 'el', auto: false } },
};
/** Meta template names: lower case letters, digits and underscores */
export const isTemplateName = (s: unknown) => typeof s === 'string' && /^[a-z0-9_]{1,512}$/.test(s);
const LANG_RE = /^[a-z]{2}(_[A-Z]{2})?$/;

export function normalizeWhatsApp(raw: unknown): WhatsAppSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const t = (o.templates && typeof o.templates === 'object' ? o.templates : {}) as Record<string, Record<string, unknown> | undefined>;
  const templates = Object.fromEntries(WHATSAPP_USES.map((u) => {
    const x = t[u] ?? {};
    const name = isTemplateName(x.name) ? String(x.name) : '';
    return [u, { name, lang: typeof x.lang === 'string' && LANG_RE.test(x.lang) ? x.lang : 'el', auto: x.auto === true && !!name }];
  })) as Record<WhatsAppUse, WhatsAppTemplateSetting>;
  const num = (v: unknown, lo: number, hi: number, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : d);
  return { enabled: o.enabled === true, templates, reminderHours: num(o.reminderHours, 2, 72, 24), renewalDays: num(o.renewalDays, 3, 60, 21) };
}
export const whatsAppSettings = (v: WorkspaceView) => normalizeWhatsApp(getSetting<unknown>(v, WHATSAPP_KEY, null));

/** the latest WhatsApp consent of a person (a later withdrawal wins) */
export function whatsAppConsent(v: WorkspaceView, personId: string): { granted: boolean; recorded: boolean; date: string | null } {
  const list = v.where('consents', 'personId', personId).filter((c) => c.purpose === WHATSAPP_CONSENT)
    .sort((a, b) => (a.date ?? a.createdAt).localeCompare(b.date ?? b.createdAt) || a.createdAt.localeCompare(b.createdAt));
  const last = list[list.length - 1];
  return { recorded: !!last, granted: !!last && last.granted && !last.withdrawnAt, date: last?.date ?? null };
}
/** E.164 digits for the Cloud API (8-digit Cypriot mobiles get 357), or null */
export function waNumber(p: Pick<Person, 'whatsapp' | 'primaryPhone'>): string | null {
  const raw = String(p.whatsapp || p.primaryPhone || '').replace(/[^\d+]/g, '').replace(/^00/, '+');
  let d = raw.replace(/^\+/, '');
  if (!raw.startsWith('+') && /^9\d{7}$/.test(d)) d = '357' + d;
  return /^[1-9]\d{7,14}$/.test(d) ? d : null;
}

export type WaBlock = 'off' | 'no_template' | 'no_consent' | 'consent_withdrawn' | 'no_number' | 'archived' | 'do_not_contact';
/** may this person get this kind of message? (the first reason it may not) */
export function whatsAppEligible(v: WorkspaceView, personId: string, use: WhatsAppUse, s = whatsAppSettings(v)): { ok: true; to: string } | { ok: false; reason: WaBlock } {
  if (!s.enabled) return { ok: false, reason: 'off' };
  if (!s.templates[use].name) return { ok: false, reason: 'no_template' };
  const p = v.get('persons', personId);
  if (!p || p.archivedAt) return { ok: false, reason: 'archived' };
  if ((p.data as { doNotContact?: boolean } | null | undefined)?.doNotContact) return { ok: false, reason: 'do_not_contact' };
  const c = whatsAppConsent(v, personId);
  if (!c.granted) return { ok: false, reason: c.recorded ? 'consent_withdrawn' : 'no_consent' };
  const to = waNumber(p);
  if (!to) return { ok: false, reason: 'no_number' };
  return { ok: true, to };
}

const firstName = (p: Person) => clean(p.firstName) || clean(p.displayName).split(/\s+/)[0] || '';
const dmy = (d: Day) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;

/** one message for the platform to send: the template and its body parameters only */
export interface WaMessage { key: string; personId: string; use: WhatsAppUse; to: string; template: string; lang: string; params: string[]; ref?: { table: string; id: string } | null }

/** the parameters for each use — {{1}} first name, then date / time / product (never amounts or numbers) */
export function whatsAppMessage(v: WorkspaceView, use: WhatsAppUse, personId: string, o: { taskId?: string; policyId?: string; dueDate?: Day; occasion?: string } = {}, s = whatsAppSettings(v)): WaMessage | { error: WaBlock | 'not_found' } {
  const e = whatsAppEligible(v, personId, use, s);
  if (!e.ok) return { error: e.reason };
  const p = v.get('persons', personId)!;
  const tpl = s.templates[use];
  let params: string[]; let ref: WaMessage['ref'] = null; let occasion = o.occasion ?? '';
  if (use === 'appointment_reminder') {
    const t = o.taskId ? v.get('tasks', o.taskId) : undefined;
    if (!t || t.personId !== personId || t.kind !== 'appointment' || !t.dueDate || !t.dueTime) return { error: 'not_found' };
    params = [firstName(p), dmy(t.dueDate), t.dueTime];
    ref = { table: 'tasks', id: t.id }; occasion = `${t.id}:${t.dueDate}T${t.dueTime}`;
  } else if (use === 'renewal_notice') {
    const pol = o.policyId ? v.get('policies', o.policyId) : undefined;
    if (!pol || pol.personId !== personId || !o.dueDate) return { error: 'not_found' };
    params = [firstName(p), clean(pol.productName).slice(0, 60) || '—', dmy(o.dueDate)];
    ref = { table: 'policies', id: pol.id }; occasion = `${pol.id}:${o.dueDate}`;
  } else {
    params = [firstName(p)];
    occasion = occasion || (o.dueDate ?? '').slice(0, 4);
  }
  if (!params[0]) return { error: 'not_found' };
  return { key: stableId('whatsapp', `${use}|${personId}|${occasion}`), personId, use, to: e.to, template: tpl.name, lang: tpl.lang, params, ref };
}

/** messages the turned-on automations would send today (once per client and occasion; already recorded ones skipped) */
export function whatsAppDue(v: WorkspaceView, today: Day, nowTime = '00:00'): WaMessage[] {
  const s = whatsAppSettings(v);
  if (!s.enabled) return [];
  const out: WaMessage[] = [];
  const add = (m: WaMessage | { error: string }) => { if ('key' in m && !v.get('communications', waCommId(m.key))) out.push(m); };
  if (s.templates.appointment_reminder.auto) {
    const now = Date.parse(`${today}T${nowTime}:00Z`);
    for (const t of v.rows('tasks')) {
      if (t.kind !== 'appointment' || t.status !== 'open' || !t.personId || !t.dueDate || !t.dueTime) continue;
      const at = Date.parse(`${t.dueDate}T${t.dueTime}:00Z`);
      const hours = (at - now) / 3_600_000;
      if (hours > 0 && hours <= s.reminderHours) add(whatsAppMessage(v, 'appointment_reminder', t.personId, { taskId: t.id }, s));
    }
  }
  if (s.templates.birthday.auto) {
    for (const p of v.rows('persons')) {
      if (p.archivedAt || p.stage === 'dependent' || !p.birthDate || p.birthDate.slice(5) !== today.slice(5)) continue;
      add(whatsAppMessage(v, 'birthday', p.id, { occasion: today.slice(0, 4) }, s));
    }
  }
  if (s.templates.renewal_notice.auto) {
    for (const r of renewalQueue(v, today, { includeLife: false })) {
      const days = diffDays(today, r.dueDate);
      if (days >= 0 && days <= s.renewalDays) add(whatsAppMessage(v, 'renewal_notice', r.policy.personId, { policyId: r.policy.id, dueDate: r.dueDate }, s));
    }
  }
  return out;
}

export const waCommId = (key: string) => stableId('whatsapp-comm', key);

/** the platform accepted (or refused) a message: one WhatsApp communication + timeline entry, once per key */
export function recordWhatsAppSend(v: WorkspaceView, m: WaMessage & { waId?: string | null; status: WaStatus; error?: string | null; auto?: boolean }, ctx: Ctx): Changeset | null {
  const id = waCommId(m.key);
  if (v.get('communications', id)) return null;
  const p = requirePerson(v, m.personId);
  if (!(WHATSAPP_USES as readonly string[]).includes(m.use)) throw new DomainError('whatsapp.use');
  const subject = `whatsapp.use.${m.use}`;
  const data = { source: 'whatsapp', key: m.key, use: m.use, template: m.template, waId: m.waId ?? null, status: m.status, statusAt: ctx.now, error: m.error ?? null, auto: !!m.auto, ref: m.ref ?? null };
  const row = created<'communications'>(ctx, { id, personId: p.id, prospectId: null, at: ctx.now, channel: 'whatsapp', direction: 'out', subject, summary: null, outcome: null, taskId: m.ref?.table === 'tasks' ? m.ref.id : null, data });
  const puts: Put[] = [put('communications', row)];
  const ev = timeline(ctx, p.id, 'message', subject, null, { table: 'communications', id });
  puts.push({ ...ev, row: { ...(ev.row as object), id: stableId('whatsapp-timeline', m.key), data: { source: 'whatsapp', status: m.status } } } as Put);
  return { action: 'whatsapp.send', entity: 'communications', entityId: id, summary: m.use, puts, deletes: [] };
}

const RANK: Record<WaStatus, number> = { queued: 0, sent: 1, delivered: 2, read: 3, failed: 4 };
/** delivery statuses from the platform (Meta webhook): only moves forward; unknown messages are ignored */
export function recordWhatsAppStatuses(v: WorkspaceView, items: readonly { key: string; status: string; at?: string; error?: string | null }[], ctx: Ctx): Changeset | null {
  const puts: Put[] = [];
  for (const it of items) {
    if (!(WA_STATUSES as readonly string[]).includes(it.status)) continue;
    const c = v.get('communications', waCommId(String(it.key)));
    if (!c) continue;
    const d = (c.data ?? {}) as { status?: WaStatus };
    const cur = d.status ?? 'queued';
    if (RANK[it.status as WaStatus] <= RANK[cur] && !(it.status === 'failed' && cur !== 'failed')) continue;
    puts.push(put('communications', touched(ctx, c, { data: { ...(c.data as object), status: it.status, statusAt: it.at ?? ctx.now, error: it.status === 'failed' ? clean(it.error).slice(0, 200) || 'failed' : null } })));
  }
  return puts.length ? { action: 'whatsapp.status', entity: 'communications', summary: String(puts.length), puts, deletes: [] } : null;
}
export const whatsAppStatusOf = (v: WorkspaceView, key: string): WaStatus | null => ((v.get('communications', waCommId(key))?.data ?? null) as { status?: WaStatus } | null)?.status ?? null;
